// MUI ICONS
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import ScheduleIcon from "@mui/icons-material/Schedule";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

// Status colors match the Automation Logs / Project Health dashboards
const RED = { fg: "#c62828", bg: "#ffebee" };
const AMBER = { fg: "#ca8a04", bg: "#fffde7" };
const BLUE = { fg: "#184c93", bg: "#e8f0fe" };
const GREEN = { fg: "#2e7d32", bg: "#e8f5e9" };
const GRAY = { fg: "#757575", bg: "#f5f5f5" };

// time_off_request_days.sync_status
export const DAY_STATUS = {
	error: { ...RED, label: "Error", group: "error" },
	manual_review: { ...AMBER, label: "Manual Review", group: "manual_review" },
	pending: { ...BLUE, label: "Pending", group: "pending" },
	created: { ...GREEN, label: "In BQE", group: "created" },
	skipped_zero: { ...GRAY, label: "Skipped (0 h)", group: "other" },
	not_posted: { ...GRAY, label: "Not Posted", group: "other" },
	deleted: { ...GRAY, label: "Removed", group: "other" },
	legacy: { ...GRAY, label: "Legacy", group: "other" },
	// set by hand: the sync leaves the day alone
	ignore: { fg: "#566472", bg: "#eef1f4", label: "Ignored", group: "other" },
};

// Days worth retrying: errors, and days the sync gave up on after its retry
// limit. Other manual reviews (locked timesheet, duplicate or existing entry
// in BQE) are re-checked by the sync every run and need a fix in BQE instead.
export const canRetryDay = (d) =>
	d.sync_status === "error" ||
	(d.sync_status === "manual_review" && Number(d.attempts) > 0);

// The sync only revisits requests in its look-back window (LOOKBACK_DAYS in the
// Lambda), so older days can't be retried from here
export const SYNC_LOOKBACK_DAYS = 28;
export const inSyncWindow = (request) => {
	const cutoff = new Date();
	cutoff.setDate(cutoff.getDate() - SYNC_LOOKBACK_DAYS);
	const iso = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, "0")}-${String(cutoff.getDate()).padStart(2, "0")}`;
	return request.end_date >= iso;
};

// Summary cards on the Requests tab; each groups one or more day statuses.
// "Other" is skipped, not posted, removed, legacy (posted by Make) and ignored.
export const DAY_GROUPS = [
	{ key: "error", label: "Error Days", icon: ErrorIcon, ...RED },
	{
		key: "manual_review",
		label: "Manual Review Days",
		icon: WarningIcon,
		...AMBER,
	},
	{ key: "pending", label: "Pending Days", icon: ScheduleIcon, ...BLUE },
	{ key: "created", label: "Days In BQE", icon: CheckCircleIcon, ...GREEN },
	{
		key: "other",
		label: "Other Days",
		icon: RemoveCircleOutlineIcon,
		...GRAY,
	},
];

// time_off_requests.sync_status
export const REQUEST_SYNC = {
	error: { ...RED, label: "Error" },
	manual_review: { ...AMBER, label: "Manual Review" },
	pending: { ...BLUE, label: "Pending" },
	synced: { ...GREEN, label: "Synced" },
	reversed: { ...GRAY, label: "Reversed" },
	skipped: { ...GRAY, label: "Skipped" },
	legacy: { ...GRAY, label: "Legacy" },
};

// BambooHR request status (lowercase in the API)
export const REQUEST_STATUS = {
	approved: GREEN,
	requested: BLUE,
	denied: RED,
	canceled: GRAY,
	superceded: GRAY,
};

// HELPERS
const MONTHS = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const parseDate = (s) => {
	const [y, m, d] = String(s).split("-").map(Number);
	return new Date(Date.UTC(y, m - 1, d));
};

// "2026-10-12" -> "Oct 12, 2026"
export const fmtDate = (s) => {
	if (!s) return "—";
	const d = parseDate(s);
	return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
};

// "2026-10-12" -> "Mon Oct 12"
export const fmtDay = (s) => {
	const d = parseDate(s);
	return `${WEEKDAYS[d.getUTCDay()]} ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
};

export const fmtDateTime = (ts) =>
	ts
		? new Date(ts).toLocaleString("en-US", {
				month: "short",
				day: "numeric",
				year: "numeric",
				hour: "numeric",
				minute: "2-digit",
			})
		: "—";

export const titleCase = (s) =>
	String(s || "")
		.toLowerCase()
		.replace(/\b\w/g, (c) => c.toUpperCase());
